/**
 * Product-analytics seam for the cabinet, backed by Plausible.
 *
 * Plausible is page-level, cookieless product analytics: traffic, funnel
 * and the ~25 `track()` moments below (install, auth, login/paywall
 * walls, cartridge lifecycle). It is NOT run telemetry. Per-run death
 * cause, level reached, FPS and resource-at-death stay in OUR database
 * (`run_telemetry` via `POST /v1/telemetry/runs`, docs/spec-telemetry.md).
 *
 * Loading is gated on `VITE_PLAUSIBLE_DOMAIN`, which CI sets ONLY on the
 * `main` deploy (.github/workflows/deploy-shell.yml). Local builds, dev
 * deploys and tests leave it unset, so the script is never injected and
 * every call below is a no-op. There is no SDK dependency: one deferred
 * <script> tag from plausible.io, plus the official queue stub so events
 * fired before it loads are not lost.
 *
 * Plausible's terms forbid personal data in custom properties, so
 * identity keys are stripped (see `IDENTITY_KEYS`) and `identify()` is
 * deliberately a no-op. Plausible has no user identity by design.
 */

type PlausibleProps = Record<string, string | number | boolean>;
type PlausibleFn = ((event: string, options?: { props?: PlausibleProps }) => void) & {
  q?: unknown[];
};

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

const SCRIPT_SRC = 'https://plausible.io/js/script.js';

/** Plausible caps custom properties at 30 per event. */
const MAX_PROPS = 30;

/** Never sent: Plausible custom props must not carry personal data. */
const IDENTITY_KEYS = new Set(['userId', 'user_id', 'email', 'distinctId', 'deviceId']);

let enabled = false;

/**
 * Inject the Plausible script once. No-op unless a domain is configured.
 * The `domain` param exists for tests; production reads the build env.
 */
export function initAnalytics(
  domain: string | undefined = import.meta.env.VITE_PLAUSIBLE_DOMAIN,
): void {
  if (enabled || !domain || typeof document === 'undefined') return;
  enabled = true;

  if (!window.plausible) {
    const q: unknown[] = [];
    const queue: PlausibleFn = Object.assign(
      (...args: unknown[]) => {
        q.push(args);
      },
      { q },
    );
    window.plausible = queue;
  }

  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = domain;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
}

/** Pick the label of the first band whose upper bound is above `n`. */
function band(n: number, bands: ReadonlyArray<readonly [number, string]>, top: string): string {
  for (const [below, label] of bands) if (n < below) return label;
  return top;
}

/** Session length, in milliseconds, as a coarse human label. */
export function durationBucket(ms: number): string {
  const s = ms / 1000;
  return band(
    s,
    [
      [10, '<10s'],
      [30, '10-30s'],
      [60, '30s-1m'],
      [180, '1-3m'],
      [300, '3-5m'],
      [600, '5-10m'],
      [1800, '10-30m'],
    ],
    '30m+',
  );
}

/**
 * Score by order of magnitude. Scales differ wildly between cartridges,
 * so read this broken down by `cartridgeId`, never across the catalog.
 */
export function scoreBucket(score: number): string {
  if (score <= 0) return '0';
  return band(
    score,
    [
      [100, '1-99'],
      [1_000, '100-999'],
      [10_000, '1k-10k'],
      [100_000, '10k-100k'],
      [1_000_000, '100k-1M'],
    ],
    '1M+',
  );
}

/** Leaderboard rank: the podium, then widening bands. */
export function rankBucket(rank: number): string {
  if (rank <= 1) return '1';
  return band(
    rank,
    [
      [4, '2-3'],
      [11, '4-10'],
      [26, '11-25'],
      [101, '26-100'],
    ],
    '101+',
  );
}

/**
 * Continuous measures become bands. Plausible stores every prop value as
 * a text label and cannot sum or average them, so a raw duration or score
 * makes one useless breakdown row per distinct value. Exact numbers live
 * in run telemetry (our database). `as` renames a key whose unit no
 * longer applies once bucketed.
 */
const BUCKETED: Record<string, { as: string; bucket: (n: number) => string }> = {
  durationMs: { as: 'duration', bucket: durationBucket },
  score: { as: 'score', bucket: scoreBucket },
  rank: { as: 'rank', bucket: rankBucket },
};

/**
 * Keep scalar, non-identifying props, banding continuous numbers.
 * Plausible rejects nested values.
 */
export function toPlausibleProps(props?: Record<string, unknown>): PlausibleProps | undefined {
  if (!props) return undefined;
  const out: PlausibleProps = {};
  let count = 0;
  for (const [key, value] of Object.entries(props)) {
    if (count >= MAX_PROPS) break;
    if (IDENTITY_KEYS.has(key)) continue;
    const bucketed = BUCKETED[key];
    if (bucketed) {
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      out[bucketed.as] = bucketed.bucket(value);
    } else if (typeof value === 'string' || typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value;
    } else {
      continue;
    }
    count++;
  }
  return count > 0 ? out : undefined;
}

/** Send a custom event. No-op when Plausible is not enabled. */
export function track(event: string, props?: Record<string, unknown>): void {
  if (!enabled || !window.plausible) return;
  const cleaned = toPlausibleProps(props);
  window.plausible(event, cleaned ? { props: cleaned } : undefined);
}

/** No-op by design: Plausible has no user identity, and we send no PII. */
export function identify(
  _distinctId: string,
  _props?: Record<string, unknown>,
): void {}

/** No-op: there is no provider-held identity to clear. */
export function resetAnalytics(): void {}
